/*
The lines that tell Ollie what the reader has open. They reach the model inside the prompt, so the
first is written for a reader of English — what the record is, where it belongs, where it stands —
and the second is the machine half: every id the screen has, named as `table.column`, because the
assistant answers through GraphQL and an id is what turns "why did this fail?" into a query it can
actually run.

Give them every id the screen holds, not only the one in the URL. A question about the report often
has to be answered from a neighbouring table — the jurisdiction's rules, the property's other
submittals — and an id it already has saves a lookup it might get wrong.

Every builder returns "" while its data is still loading: an id with no name attached tells the
model less than saying nothing, and the reader can ask a question before the page has finished.
*/

// The ids every Ollie screen carries, whatever record it is showing.
export interface OllieScreenScope {
	propertyId?: string | null;
	jurisdictionId?: string | null;
	organizationId?: string | null;
	workspaceId?: string | null;
	// Who opened the record. `users.id` is a Firebase uid, not a uuid.
	createdByUserId?: string | null;
}

// Drops the parts a record does not have, so a half-loaded row never reads as "undefined".
const sentence = (parts: Array<string | null | undefined | false>): string =>
	parts
		.filter(Boolean)
		.join(" ")
		.replace(/\s+/g, " ")
		.replace(/\s+([,.])/g, "$1")
		.trim();

/*
The ids, written the way they are queried. `table.column = value`, separated by semicolons, so the
model can lift one straight into a `where` clause instead of inferring which table a bare uuid
belongs to. An id the screen does not have is left out rather than sent as null.
*/
const identifiers = (entries: Array<[string, string | number | null | undefined]>): string => {
	const known = entries.filter(([, value]) => value !== null && value !== undefined && value !== "");

	if (!known.length) return "";

	return `Records on this screen, to look anything up by: ${known
		.map(([column, value]) => `${column} = ${value}`)
		.join("; ")}.`;
};

const scopeIdentifiers = (scope: OllieScreenScope): Array<[string, string | null | undefined]> => [
	["properties.id", scope.propertyId],
	["jurisdictions.id", scope.jurisdictionId],
	["organizations.id", scope.organizationId],
	["workspaces.id", scope.workspaceId],
	["users.id (who opened it)", scope.createdByUserId]
];

interface PlanReviewJobDescription extends OllieScreenScope {
	jobId?: string | null;
	planReviewId?: string | null;
	submissionTypeId?: string | null;
	submissionNumber?: number | null;
	propertyName?: string | null;
	jurisdictionName?: string | null;
	submissionTypeName?: string | null;
	reviewType?: string | null;
	state?: string | null;
	reviewOutcome?: string | null;
	complianceScore?: number | null;
	documentCount?: number | null;
	findingCount?: number | null;
	// True on the comments tab, which is the same submittal read as a list of what it still has
	// to answer for rather than as a report.
	isCommentsView?: boolean;
}

export const buildPlanReviewJobDescription = ({
	jobId,
	planReviewId,
	submissionTypeId,
	submissionNumber,
	propertyName,
	jurisdictionName,
	submissionTypeName,
	reviewType,
	state,
	reviewOutcome,
	complianceScore,
	documentCount,
	findingCount,
	isCommentsView = false,
	...scope
}: PlanReviewJobDescription): string => {
	if (!jobId) return "";

	const summary = sentence([
		isCommentsView ? "The reviewer comments on" : "The plan review report for",
		submissionNumber ? `submittal #${submissionNumber}` : "a submittal",
		propertyName && `at ${propertyName}`,
		jurisdictionName && `under ${jurisdictionName}`,
		submissionTypeName && `, filed as ${submissionTypeName}`,
		reviewType && `(${reviewType} review)`,
		".",
		state && `Its state is ${state}.`,
		reviewOutcome && `The outcome is ${reviewOutcome}.`,
		typeof complianceScore === "number" && `Its compliance score is ${complianceScore}.`,
		typeof documentCount === "number" &&
			`The package has ${documentCount} ${documentCount === 1 ? "document" : "documents"}.`,
		typeof findingCount === "number" &&
			(isCommentsView
				? `There are ${findingCount} ${findingCount === 1 ? "comment" : "comments"} on it.`
				: `The report has ${findingCount} ${findingCount === 1 ? "finding" : "findings"}.`)
	]);

	return `${summary}\n${identifiers([
		["plan_review_jobs.id", jobId],
		["plan_reviews.id", planReviewId],
		["plan_review_submission_types.id", submissionTypeId],
		...scopeIdentifiers(scope)
	])}`.trim();
};

interface PlanReviewDescription extends OllieScreenScope {
	planReviewId?: string | null;
	submissionTypeId?: string | null;
	propertyName?: string | null;
	jurisdictionName?: string | null;
	submissionTypeName?: string | null;
	reviewType?: string | null;
	// Every submittal of this project, newest first, so a question about "the last one" can be
	// answered without reading the list again.
	submissionJobIds?: string[];
}

export const buildPlanReviewDescription = ({
	planReviewId,
	submissionTypeId,
	propertyName,
	jurisdictionName,
	submissionTypeName,
	reviewType,
	submissionJobIds = [],
	...scope
}: PlanReviewDescription): string => {
	if (!planReviewId) return "";

	const count = submissionJobIds.length;

	const summary = sentence([
		"The list of submittals for the plan review project",
		propertyName && `at ${propertyName}`,
		jurisdictionName && `under ${jurisdictionName}`,
		submissionTypeName && `, filed as ${submissionTypeName}`,
		reviewType && `(${reviewType} review)`,
		".",
		count ? `It has ${count} ${count === 1 ? "submittal" : "submittals"} so far.` : "It has no submittals yet."
	]);

	return `${summary}\n${identifiers([
		["plan_reviews.id", planReviewId],
		["plan_review_submission_types.id", submissionTypeId],
		...scopeIdentifiers(scope),
		// Newest first, which is the order the screen lists them in.
		["plan_review_jobs.id (newest first)", count ? submissionJobIds.join(", ") : null]
	])}`.trim();
};

interface InspectionJobDescription extends OllieScreenScope {
	jobId?: string | null;
	propertyName?: string | null;
	jurisdictionName?: string | null;
	inspectionType?: string | null;
	state?: string | null;
	irisScore?: number | null;
	checklistItemCount?: number | null;
	// True on the evidence-collection screen, where the reader is still gathering photos rather
	// than reading a finished report. It changes what "this" is likely to mean.
	isCollecting?: boolean;
}

export const buildInspectionJobDescription = ({
	jobId,
	propertyName,
	jurisdictionName,
	inspectionType,
	state,
	irisScore,
	checklistItemCount,
	isCollecting = false,
	...scope
}: InspectionJobDescription): string => {
	if (!jobId) return "";

	const summary = sentence([
		isCollecting ? "The evidence collection screen for" : "The readiness report for",
		inspectionType ? `a ${inspectionType} inspection` : "an inspection",
		propertyName && `at ${propertyName}`,
		jurisdictionName && `under ${jurisdictionName}`,
		".",
		state && `Its state is ${state}.`,
		typeof irisScore === "number" && `Its readiness score is ${irisScore}.`,
		typeof checklistItemCount === "number" &&
			`Its checklist has ${checklistItemCount} ${checklistItemCount === 1 ? "item" : "items"}.`,
		isCollecting && "The reader is working through those items right now."
	]);

	return `${summary}\n${identifiers([["inspection_jobs.id", jobId], ...scopeIdentifiers(scope)])}`.trim();
};

interface ScreenDescription extends OllieScreenScope {
	/*
	One or two sentences naming what the screen is, written the way the reader would describe it.
	A screen with no single record still tells the assistant a lot: somebody on the rules screen is
	asking about the rules they maintain, and somebody starting a submittal is asking what to file.
	*/
	screen: string;
	// Ids this screen has beyond the shared scope, as `table.column` pairs.
	ids?: Array<[string, string | number | null | undefined]>;
}

// Describes a screen that is a list, a configuration page or a wizard rather than one record.
export const buildScreenDescription = ({ screen, ids = [], ...scope }: ScreenDescription): string => {
	if (!screen) return "";

	return `${screen}\n${identifiers([...ids, ...scopeIdentifiers(scope)])}`.trim();
};
